// library imports
import {
  ActorProfilePreviewFragmentFragment,
  NotificationSettingNotifier,
  useFollowMutation,
  useNotificationSettingsForSubject,
  useUnfollowMutation
} from '@lopo-tech/lopo-api'
import React, { useState } from 'react'

// components
import Icon from 'src/components/forms/icon/icon'
import MessageBoxWithModal from 'src/components/message_box/message_box_with_modal'
import Popover, { POPOVER_LAYOUT_PLAIN } from 'src/components/popover/popover'
import UserOnboardingCTA from 'src/containers/smart_components/user_onboarding_cta/user_onboarding_cta'
import FollowButtonMenu from './follow_popover_menu'

// constants
import { IRenderAPI } from 'src/components/_libs/popover_lib/popover_lib'
import {
  MSGBOX_BUTTON_TYPE_CANCEL,
  MSGBOX_BUTTON_TYPE_DANGER
} from 'src/components/message_box/message_box'

// utils
import { textResources } from 'src/lang/de'
import { createBemBlock } from 'src/utils/bem_helper/bem_helper'
import initTracker, {
  FOLLOW_BUTTON
} from 'src/utils/reporting/events_tracking/events_tracking'

// styles
import { useDispatch } from 'react-redux'
import { AppDispatch, showSnackBar } from 'src/actions/app-state/app-state'
import './follow_button.scss'
const label = textResources.follow

const tracker = initTracker(FOLLOW_BUTTON)

export interface IProps {
  actorProfile: ActorProfilePreviewFragmentFragment;
}
const cls = createBemBlock('follow_button')

const FollowButton: React.FC<IProps> = ({
  actorProfile: { id, canFollow, name, isFollowing }
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [isVisibleCancelModal, showCancelModal] = useState(false)
  const [mutateFollow] = useFollowMutation()
  const [mutateUnfollow] = useUnfollowMutation()

  const { data } = useNotificationSettingsForSubject({
    variables: {
      notifier: NotificationSettingNotifier.NewFolloweePost,
      subjectId: id
    },
    skip: !canFollow.value
  })

  if (!canFollow.value) {
    return null
  }
  const notificationSettings = data?.notificationSettingsForSubject

  const notifiesMe = notificationSettings?.some(
    (setting) => setting.isEnabled
  )

  const follow = () => {
    tracker(FOLLOW_BUTTON.ACTIONS.FOLLOW, FOLLOW_BUTTON.LABELS.START)

    mutateFollow({
      variables: { input: { followeeId: id } }
    })
      .then(() => {
        dispatch(
          showSnackBar({
            message: textResources.follow.followAlertMessage(name)
          })
        )
      })
      .catch((error) => {
        dispatch(
          showSnackBar({
            message: error.message || textResources.shared.genericErrorLong
          })
        )
      })
  }

  const unFollow = () => {
    tracker(FOLLOW_BUTTON.ACTIONS.UNFOLLOW, FOLLOW_BUTTON.LABELS.START)
    mutateUnfollow({
      variables: { input: { followeeId: id } }
    })
    showCancelModal(false)
  }

  return (
    <UserOnboardingCTA active={!canFollow.value}>
      {isFollowing
        ? (
          <Popover
            isOpen={isVisibleCancelModal}
            position={'top'}
            align={'relative'}
            layout={POPOVER_LAYOUT_PLAIN}
            trigger={
              <button className={cls()}>
                <p>
                  {label.following}{' '}
                  <Icon className={cls('icon')} name="arrow-down" size={12} />
                </p>
              </button>
            }
          >
            {({ hide }: IRenderAPI) => (
              <FollowButtonMenu
                hide={hide}
                showCancelModal={showCancelModal}
                isNotifying={!!notifiesMe}
                profileId={id}
              />
            )}
          </Popover>
        )
        : (
          <button
            className={cls()}
            onClick={follow}
          >
            <p>{label.follow}</p>
          </button>
        )}

      {isVisibleCancelModal && (
        <MessageBoxWithModal
          buttons={[
            {
              label: label.unfollow,
              onClick: unFollow,
              type: MSGBOX_BUTTON_TYPE_DANGER
            },
            {
              label: label.following,
              onClick: () => {
                showCancelModal(false)
              },
              type: MSGBOX_BUTTON_TYPE_CANCEL
            }
          ]}
          title={label.unfollowAlertTitle}
        >
          <p
            dangerouslySetInnerHTML={{
              __html: label.unfollowAlertBody(`<strong>${name}</strong>`)
            }}
          />
        </MessageBoxWithModal>
      )}
    </UserOnboardingCTA>
  )
}

export default FollowButton
